import { h } from "preact";
import { renderIsland } from "../../ui/renderIsland";

/**
 * One group in the filter select.
 *
 * Partner groups use uuid ids (string); artist groups still use int ids — both
 * are accepted, as the string builder this replaced did.
 */
export interface GroupSelectOption {
  id: string | number;
  name: string;
  /** Comma-separated member ids (the wire format). */
  members: string;
}

/**
 * The reseller/artist group filter select plus its membership markers.
 *
 * ⚠️ The `group-<groupId>-<memberId>` markers MUST render as **siblings** of the
 * `<select>`, never inside it: an `<input>` inside a `<select>` is a parse error
 * and the HTML parser discards it. This builder always got that right; the
 * server-side twin in `dashboardData.ts` did not, which silently broke the
 * dashboard group filter — `resellerDashboard.ts` found no members and hid every
 * row. Keep them outside the select.
 *
 * No `onChange` here on purpose: `features/groups.ts` binds a document-level
 * `delegate("change", "#groupselect", …)` that covers this select wherever it is
 * inserted. Adding a handler here would run `onlygroup` twice.
 */
export function GroupSelect({ groups, label }: { groups: GroupSelectOption[]; label: string }) {
  return (
    <>
      <select id="groupselect" class="select-button">
        <option value="">{label}</option>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
      {groups.flatMap((group) =>
        group.members
          .split(",")
          .filter(Boolean)
          .map((memberId) => (
            <input
              key={`${group.id}-${memberId}`}
              type="hidden"
              id={`group-${group.id}-${memberId}`}
            />
          ))
      )}
    </>
  );
}

/**
 * Re-render the group select into `#partnergroup-select`.
 *
 * Replaces eight copies of the same
 * `if (getDomElement(…)) setHtml(…, buildGroupSelectHtml(…))` pair; the
 * container check is `renderIsland`'s job now.
 */
export function renderGroupSelect(groups: GroupSelectOption[], label: string): void {
  renderIsland("partnergroup-select", h(GroupSelect, { groups, label }));
}
